/**
 * API Endpoint Registry
 *
 * Central registry mapping API endpoints to their Zod response schemas.
 * Used by:
 * - Runtime response validation in web app (dev/staging only)
 * - Contract tests in e2e suite
 *
 * @module api/endpoint-registry
 */

import { z, ZodSchema } from 'zod';
import { LoginResponseSchema, RegisterResponseSchema } from '../schemas/auth';
import {
  BookingResponseSchema,
  AvailabilityResponseSchema,
  PriceBreakdownSchema,
} from '../schemas/bookings';
import {
  CheckInLookupResultsSchema,
  TodayArrivalsResponseSchema,
  EnhancedCheckInResponseSchema,
} from '../types/check-in';
import { VenueResponseSchema, PaginatedVenuesResponseSchema } from '../schemas/venues';
import {
  CouponResponseSchema,
  CouponValidationResultSchema,
  AvailableCouponsResultSchema,
  PaginatedCouponsResponseSchema,
} from '../schemas/coupons';
import {
  CommissionInvoiceListResponseSchema,
  CommissionInvoiceResponseSchema,
} from '../schemas/invoices';
import { ReferralStatsResponseSchema } from '../types/referral';
import { PublicPricingRatesSchema } from '../schemas/settings';
import { PlatformStatsSchema } from '../schemas/stats';
import { createFlatPaginatedSchema } from '../types/api-response';

// Create response-specific schema for amenity config
// (API returns ISO strings for dates, not Date objects)
const AmenityConfigResponseSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  displayOrder: z.number(),
  isActive: z.boolean(),
  description: z.string().optional().nullable(),
  icon: z.string().optional().nullable(),
  category: z.string().optional().nullable(),
  venueTypes: z.array(z.string()),
  createdAt: z.string(), // ISO string from API
  updatedAt: z.string(), // ISO string from API
});

// City response schema for popular cities endpoint
// (API returns ISO strings for dates, not Date objects)
const CityResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  stateId: z.string(),
  servicePincodes: z.string().nullable().optional(),
  isPopular: z.boolean(),
  displayOrder: z.number(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  isActive: z.boolean(),
  createdAt: z.string(), // ISO string from API
  updatedAt: z.string(), // ISO string from API
  state: z
    .object({
      id: z.string(),
      name: z.string(),
      code: z.string(),
      displayOrder: z.number(),
      isActive: z.boolean(),
      createdAt: z.string(),
      updatedAt: z.string(),
    })
    .optional(),
});

// Venue type config response schema for config endpoint
// (API returns ISO strings for dates, not Date objects)
const VenueTypeConfigResponseSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  displayOrder: z.number(),
  isActive: z.boolean(),
  description: z.string().optional().nullable(),
  icon: z.string().optional().nullable(),
  color: z.string().optional().nullable(),
  createdAt: z.string(), // ISO string from API
  updatedAt: z.string(), // ISO string from API
});

// Search suggestion schema for autocomplete
const SearchSuggestionSchema = z.object({
  id: z.string(),
  name: z.string(),
  city: z.string().optional().nullable(),
  state: z.string().optional().nullable(),
  type: z.string().optional().nullable(),
});

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface EndpointDefinition {
  method: HttpMethod;
  path: string;
  responseSchema: ZodSchema;
  requiresAuth?: boolean;
  description?: string;
}

/**
 * Endpoint registry - maps API endpoints to their response schemas
 */
export const ENDPOINT_REGISTRY: Record<string, EndpointDefinition> = {
  // ============================================
  // AUTH ENDPOINTS
  // ============================================
  'auth.login': {
    method: 'POST',
    path: '/auth/login',
    responseSchema: LoginResponseSchema,
    requiresAuth: false,
    description: 'User login',
  },
  'auth.register': {
    method: 'POST',
    path: '/auth/register',
    responseSchema: RegisterResponseSchema,
    requiresAuth: false,
    description: 'User registration',
  },
  'auth.me': {
    method: 'GET',
    path: '/auth/me',
    responseSchema: z.object({
      id: z.string(),
      email: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      role: z.enum(['CUSTOMER', 'VENDOR', 'VENDOR_STAFF', 'ADMIN', 'SUPER_ADMIN']),
      isEmailVerified: z.boolean(),
      phone: z.string().optional(),
      createdAt: z.string(),
      updatedAt: z.string(),
    }),
    requiresAuth: true,
    description: 'Get current user',
  },

  // ============================================
  // BOOKING ENDPOINTS
  // ============================================
  'bookings.list': {
    method: 'GET',
    path: '/bookings',
    responseSchema: createFlatPaginatedSchema(BookingResponseSchema),
    requiresAuth: true,
    description: 'List user bookings',
  },
  'bookings.getById': {
    method: 'GET',
    path: '/bookings/:id',
    responseSchema: BookingResponseSchema,
    requiresAuth: true,
    description: 'Get booking by ID',
  },
  'bookings.create': {
    method: 'POST',
    path: '/bookings',
    responseSchema: BookingResponseSchema,
    requiresAuth: true,
    description: 'Create new booking',
  },
  'bookings.availability': {
    method: 'GET',
    path: '/bookings/availability/:venueId',
    responseSchema: AvailabilityResponseSchema,
    requiresAuth: false,
    description: 'Get venue availability',
  },
  'bookings.priceQuote': {
    method: 'POST',
    path: '/bookings/price-quote',
    responseSchema: PriceBreakdownSchema,
    requiresAuth: false,
    description: 'Get price quote for booking',
  },
  'bookings.cancel': {
    method: 'PUT',
    path: '/bookings/:id/cancel',
    responseSchema: BookingResponseSchema,
    requiresAuth: true,
    description: 'Cancel a booking',
  },
  'bookings.venueBookings': {
    method: 'GET',
    path: '/bookings/venue/:venueId',
    responseSchema: createFlatPaginatedSchema(BookingResponseSchema),
    requiresAuth: true,
    description: 'Get venue bookings (vendor)',
  },

  // ============================================
  // VENDOR CHECK-IN LOOKUP ENDPOINTS
  // ============================================
  'bookings.vendor.lookupByCode': {
    method: 'GET',
    path: '/bookings/vendor/lookup/code/:code',
    responseSchema: CheckInLookupResultsSchema,
    requiresAuth: true,
    description: 'Look up booking by confirmation code (vendor)',
  },
  'bookings.vendor.lookupByPhone': {
    method: 'GET',
    path: '/bookings/vendor/lookup/phone',
    responseSchema: CheckInLookupResultsSchema,
    requiresAuth: true,
    description: 'Look up bookings by customer phone (vendor)',
  },
  'bookings.vendor.lookupByName': {
    method: 'GET',
    path: '/bookings/vendor/lookup/name',
    responseSchema: CheckInLookupResultsSchema,
    requiresAuth: true,
    description: 'Search bookings by customer name (vendor)',
  },
  'bookings.vendor.todayArrivals': {
    method: 'GET',
    path: '/bookings/vendor/arrivals/today',
    responseSchema: TodayArrivalsResponseSchema,
    requiresAuth: true,
    description: "Get today's arrivals for vendor (vendor)",
  },
  'bookings.vendor.checkIn': {
    method: 'POST',
    path: '/bookings/:id/check-in',
    responseSchema: EnhancedCheckInResponseSchema,
    requiresAuth: true,
    description: 'Check in a booking (vendor)',
  },

  // ============================================
  // VENUE ENDPOINTS
  // ============================================
  'venues.list': {
    method: 'GET',
    path: '/venues',
    responseSchema: PaginatedVenuesResponseSchema,
    requiresAuth: false,
    description: 'List venues',
  },
  'venues.admin': {
    method: 'GET',
    path: '/venues/admin',
    responseSchema: PaginatedVenuesResponseSchema,
    requiresAuth: true,
    description: 'List all venues (admin)',
  },
  'venues.pending': {
    method: 'GET',
    path: '/venues/pending',
    responseSchema: PaginatedVenuesResponseSchema,
    requiresAuth: true,
    description: 'Get pending venues (admin)',
  },
  'venues.my': {
    method: 'GET',
    path: '/venues/my',
    responseSchema: PaginatedVenuesResponseSchema,
    requiresAuth: true,
    description: 'Get vendor venues',
  },
  'venues.getById': {
    method: 'GET',
    path: '/venues/:id',
    responseSchema: VenueResponseSchema,
    requiresAuth: false,
    description: 'Get venue by ID or slug',
  },
  'venues.create': {
    method: 'POST',
    path: '/venues',
    responseSchema: VenueResponseSchema,
    requiresAuth: true,
    description: 'Create venue (admin/vendor)',
  },
  'venues.update': {
    method: 'PUT',
    path: '/venues/:id',
    responseSchema: VenueResponseSchema,
    requiresAuth: true,
    description: 'Update venue',
  },

  // ============================================
  // COUPON ENDPOINTS
  // ============================================
  'coupons.available': {
    method: 'GET',
    path: '/coupons/available',
    responseSchema: AvailableCouponsResultSchema,
    requiresAuth: false,
    description: 'Get available coupons for booking',
  },
  'coupons.validate': {
    method: 'POST',
    path: '/coupons/validate',
    responseSchema: CouponValidationResultSchema,
    requiresAuth: false,
    description: 'Validate coupon code',
  },
  'coupons.list': {
    method: 'GET',
    path: '/coupons',
    responseSchema: PaginatedCouponsResponseSchema,
    requiresAuth: true,
    description: 'List coupons (vendor/admin)',
  },
  'coupons.getById': {
    method: 'GET',
    path: '/coupons/:id',
    responseSchema: CouponResponseSchema,
    requiresAuth: true,
    description: 'Get coupon by ID',
  },
  'coupons.create': {
    method: 'POST',
    path: '/coupons',
    responseSchema: CouponResponseSchema,
    requiresAuth: true,
    description: 'Create coupon (vendor/admin)',
  },
  'coupons.update': {
    method: 'PUT',
    path: '/coupons/:id',
    responseSchema: CouponResponseSchema,
    requiresAuth: true,
    description: 'Update coupon',
  },

  // ============================================
  // REFERRAL ENDPOINTS
  // ============================================
  'referral.stats': {
    method: 'GET',
    path: '/referral/stats',
    responseSchema: ReferralStatsResponseSchema,
    requiresAuth: true,
    description: 'Get user referral stats',
  },

  // ============================================
  // CONFIG ENDPOINTS
  // ============================================
  'config.amenities': {
    method: 'GET',
    path: '/config/amenities',
    responseSchema: z.array(AmenityConfigResponseSchema),
    requiresAuth: false,
    description: 'Get amenity config options',
  },

  // ============================================
  // SEARCH ENDPOINTS
  // ============================================
  'search.suggest': {
    method: 'GET',
    path: '/search/suggest',
    responseSchema: z.array(SearchSuggestionSchema),
    requiresAuth: false,
    description: 'Search venue suggestions for autocomplete',
  },

  // ============================================
  // SETTINGS ENDPOINTS
  // ============================================
  'settings.pricingRates': {
    method: 'GET',
    path: '/settings/public/pricing-rates',
    responseSchema: PublicPricingRatesSchema,
    requiresAuth: false,
    description: 'Get public pricing rates',
  },

  // ============================================
  // LOCATION ENDPOINTS
  // ============================================
  'locations.popularCities': {
    method: 'GET',
    path: '/locations/cities/popular',
    responseSchema: z.array(CityResponseSchema),
    requiresAuth: false,
    description: 'Get popular cities',
  },

  // ============================================
  // STATS ENDPOINTS
  // ============================================
  'stats.platform': {
    method: 'GET',
    path: '/stats/platform',
    responseSchema: PlatformStatsSchema,
    requiresAuth: false,
    description: 'Get platform-wide statistics',
  },

  // ============================================
  // COMMISSION INVOICE ENDPOINTS
  // ============================================
  'commissionInvoices.list': {
    method: 'GET',
    path: '/vendors/me/commission-invoices',
    responseSchema: CommissionInvoiceListResponseSchema,
    requiresAuth: true,
    description: 'List vendor commission invoices',
  },
  'commissionInvoices.getById': {
    method: 'GET',
    path: '/vendors/me/commission-invoices/:id',
    responseSchema: CommissionInvoiceResponseSchema,
    requiresAuth: true,
    description: 'Get commission invoice by ID',
  },

  // ============================================
  // VENUE TYPE CONFIG ENDPOINTS
  // ============================================
  'config.venueTypes': {
    method: 'GET',
    path: '/config/venue-types',
    responseSchema: z.array(VenueTypeConfigResponseSchema),
    requiresAuth: false,
    description: 'Get venue type config options',
  },

  // ============================================
  // OFFLINE ANALYTICS — VENDOR ENDPOINTS
  // ============================================
  'analytics.vendor.offlineRevenue': {
    method: 'GET',
    path: '/analytics/vendor/offline-revenue',
    responseSchema: z.unknown(),
    requiresAuth: true,
    description: 'Vendor offline revenue stats',
  },
  'analytics.vendor.revenueHeatmap': {
    method: 'GET',
    path: '/analytics/vendor/revenue-heatmap',
    responseSchema: z.unknown(),
    requiresAuth: true,
    description: 'Vendor revenue heatmap data',
  },
  'analytics.vendor.guestConversions': {
    method: 'GET',
    path: '/analytics/vendor/guest-conversions',
    responseSchema: z.unknown(),
    requiresAuth: true,
    description: 'Vendor guest conversion stats',
  },
  'analytics.vendor.taxSummary': {
    method: 'GET',
    path: '/analytics/vendor/tax-summary',
    responseSchema: z.unknown(),
    requiresAuth: true,
    description: 'Vendor tax summary (GSTR1)',
  },
  'analytics.vendor.revenueTargetGet': {
    method: 'GET',
    path: '/analytics/vendor/revenue-target',
    responseSchema: z.unknown(),
    requiresAuth: true,
    description: 'Get vendor monthly revenue target',
  },
  'analytics.vendor.revenueTargetSet': {
    method: 'PUT',
    path: '/analytics/vendor/revenue-target',
    responseSchema: z.unknown(),
    requiresAuth: true,
    description: 'Set vendor monthly revenue target',
  },
  'analytics.vendor.insights': {
    method: 'GET',
    path: '/analytics/vendor/insights',
    responseSchema: z.unknown(),
    requiresAuth: true,
    description: 'Vendor smart insights',
  },
  'analytics.vendor.gstr1Export': {
    method: 'GET',
    path: '/analytics/vendor/gstr1-export',
    responseSchema: z.unknown(),
    requiresAuth: true,
    description: 'Vendor GSTR1 CSV export',
  },

  // ============================================
  // OFFLINE ANALYTICS — ADMIN ENDPOINTS
  // ============================================
  'analytics.admin.bookingSourceDistribution': {
    method: 'GET',
    path: '/analytics/admin/booking-source-distribution',
    responseSchema: z.unknown(),
    requiresAuth: true,
    description: 'Admin booking source distribution',
  },
  'analytics.admin.earningPotential': {
    method: 'GET',
    path: '/analytics/admin/earning-potential',
    responseSchema: z.unknown(),
    requiresAuth: true,
    description: 'Admin offline earning potential',
  },
  'analytics.admin.guestConversions': {
    method: 'GET',
    path: '/analytics/admin/guest-conversions',
    responseSchema: z.unknown(),
    requiresAuth: true,
    description: 'Admin guest conversion stats',
  },
  'analytics.admin.vendorAdoption': {
    method: 'GET',
    path: '/analytics/admin/vendor-adoption',
    responseSchema: z.unknown(),
    requiresAuth: true,
    description: 'Admin vendor offline adoption list',
  },
};

/**
 * Find endpoint definition by method and path
 * Handles path parameters (e.g., /venues/:id matches /venues/abc-123)
 */
export function findEndpoint(method: HttpMethod, path: string): EndpointDefinition | undefined {
  // Remove query string and leading/trailing slashes
  const cleanPath = path.split('?')[0].replace(/^\/+|\/+$/g, '');

  return Object.values(ENDPOINT_REGISTRY).find((ep) => {
    if (ep.method !== method) return false;

    // Convert path params like :id to regex pattern
    const pattern = ep.path
      .replace(/^\/+|\/+$/g, '') // Remove leading/trailing slashes
      .replace(/:\w+/g, '[^/]+'); // Replace :param with regex

    const regex = new RegExp(`^${pattern}$`);
    return regex.test(cleanPath);
  });
}

/**
 * Get all endpoints for a domain (e.g., 'bookings', 'venues')
 */
export function getEndpointsByDomain(domain: string): EndpointDefinition[] {
  return Object.entries(ENDPOINT_REGISTRY)
    .filter(([key]) => key.startsWith(`${domain}.`))
    .map(([, def]) => def);
}
